import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import WithdrawApi from "../api/withdraw";
import WithdrawPrintApi from "../api/withdraw-print";
import PurchaseApi from "../api/purchase";
import AuthContext from "../components/auth-context";
import { localizeNumbers, getMoneyText } from "../functions";
import moment from "moment-jalaali";

interface Commission {
  agentTitle: string;
  amount: number;
  description?: string;
}

interface ReceiptItem {
  ProductId: number;
  Count: number;
}

interface Product {
  identifier: number;
  title: string;
  unit?: string;
  price: number;
}
interface Receipt {
  ReceiptItems: ReceiptItem[];
}

interface IWithdrawDetail {
  WithdrawType?: string;
  DetailLink?: string;
  Id?: number;
  RawAmount?: number;
  TaxAmount?: number;
  Discount?: number;
  TotalAmount?: number;
  Commissions: Commission[];
  DateTime?: number;
  Description?: string;
  ReceiptItems: ReceiptItem[];
  Products: Product[];
  HasPrintWithdrawResource: boolean;
}

function Withdraws() {
  const { id } = useParams<{ id: string }>();
  const context = useContext(AuthContext);

  const [model, setModel] = useState<IWithdrawDetail>({
    WithdrawType: undefined,
    DetailLink: undefined,
    Id: undefined,
    RawAmount: undefined,
    TaxAmount: undefined,
    Discount: undefined,
    TotalAmount: undefined,
    Commissions: [],
    DateTime: undefined,
    Description: undefined,
    ReceiptItems: [],
    Products: [],
    HasPrintWithdrawResource: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isNotDelivered, setIsNotDelivered] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [deliverError, setDeliverError] = useState<string | null>(null);
  const [deliverSuccess, setDeliverSuccess] = useState(false);
  const [receiptId, setReceiptId] = useState<string | null>(null);

  const getToman = (value: number) => {
    const money = getMoneyText(value);
    return `${money.amount} ${money.postFix}`;
  };

  useEffect(() => {
    if (!id) return;

    new WithdrawApi(context)
      .get(Number(id))
      .then((data) => {
        setModel({
          Id: data.id,
          RawAmount: data.rawAmount,
          TaxAmount: data.taxAmount,
          Discount: data.discount,
          TotalAmount: data.totalAmount,
          DateTime: Number(data.dateTime),
          Description: data.description,
          HasPrintWithdrawResource: data.hasPrintWithdrawResource,
          Commissions: data.commissions || [],
          ReceiptItems: (data.receipt || []).map((r: any) => ({
            ProductId: r.productId,
            Count: 1,
          })),
          Products: data.products || [],
        });

        setReceiptId(data.receiptId);
      })
      .catch(() => setErrorMessage("خطا در دریافت اطلاعات"));
  }, [id, context]);

  useEffect(() => {
    if (!receiptId) return;

    new PurchaseApi(context)
      .get(receiptId)
      .then((res) => {
        if (res.isPayed && !res.isDelivered) setIsNotDelivered(true);
      })
      .catch(() => setErrorMessage("خطا در دریافت وضعیت خرید"));
  }, [receiptId, context]);

  const handlePrintInvoice = () => {
    setIsLoading(true);
    setErrorMessage(null);

    new WithdrawPrintApi(context)
      .post(Number(id))
      .then((fileResult) => {
        if (!fileResult?.fileContents) return;

        const bytes = Uint8Array.from(atob(fileResult.fileContents), (c) =>
          c.charCodeAt(0)
        );

        const blob = new Blob([bytes], {
          type: fileResult.contentType,
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileResult.fileDownloadName || `${id}.docx`;
        link.click();
      })
      .catch(() => setErrorMessage("خطا در چاپ فاکتور"))
      .finally(() => setIsLoading(false));
  };

  const handleDeliverPurchase = () => {
    if (!receiptId) return;

    setDeliverLoading(true);
    setDeliverError(null);

    new PurchaseApi(context)
      .post(receiptId)
      .then(() => {
        setDeliverSuccess(true);
        setIsNotDelivered(false);
      })
      .catch(() => setDeliverError("خطا در تحویل خرید"))
      .finally(() => setDeliverLoading(false));
  };

  return (
    <div className="p-6">
      <table className="w-full border-collapse border border-gray-300 rounded-lg bg-white shadow-md">
        <tbody>
          {[
            { label: "مبلغ", value: model?.RawAmount },
            { label: "مالیات", value: model?.TaxAmount },
            { label: "تخفیف", value: model?.Discount },
            { label: "مبلغ کل", value: model?.TotalAmount },
          ].map((row, index) => (
            <tr key={index} className="border-b border-gray-200">
              <td className="px-6 py-4 text-right font-medium">{row.label}</td>
              <td className="px-6 py-4 text-left">
                {getToman(row.value!) || "-"}
              </td>
            </tr>
          ))}
          <tr>
            <td className="px-6 py-4 text-right font-medium">زمان</td>
            <td className="px-6 py-4 text-left">
              {localizeNumbers(
                moment(model.DateTime, "jYYYYjMMjDD").format("jYYYY/jMM/jDD")
              )}
            </td>
          </tr>
          <tr>
            <td className="px-6 py-4 text-right font-medium">توضیحات</td>
            <td className="px-6 py-4 text-left">
              {localizeNumbers(model?.Description!) || "-"}
            </td>
          </tr>
        </tbody>
      </table>

      {/* {model?.Commissions?.length > 0 && (
        <table className="w-full mt-6 border-collapse border border-gray-300 bg-white shadow-md">
          <tbody>
            {model.Commissions.map((commission, index) => (
              <tr key={index} className="border-b border-gray-200">
                <td className="px-6 py-4 text-right font-medium">
                  کمیسیون {commission.agentTitle}
                </td>
                <td className="px-6 py-4 text-left">{commission.amount}</td>
                {commission.description && (
                  <td className="px-6 py-4">{commission.description}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )} */}

      {model.ReceiptItems.length > 0 && (
        <table className="w-full mt-6 border border-gray-300 bg-white">
          <thead className="bg-gray-200">
            <tr>
              {["محصول", "تعداد", "قیمت", "مبلغ"].map((h) => (
                <th key={h} className="px-4 py-2 text-right">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {model.ReceiptItems.map((item, i) => {
              const product = model.Products.find(
                (p) => p.identifier === item.ProductId
              );

              if (!product) return null;

              return (
                <tr key={i} className="border-t">
                  <td className="px-4 py-2">{product.title}</td>
                  <td className="px-4 py-2">{item.Count}</td>
                  <td className="px-4 py-2">{getToman(product.price)}</td>
                  <td className="px-4 py-2">
                    {getToman(product.price * item.Count)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {model.HasPrintWithdrawResource && (
        <div className="mt-6 text-center">
          <button
            onClick={handlePrintInvoice}
            className="px-6 py-3 text-white bg-blue-600 rounded-lg shadow hover:bg-blue-700 disabled:bg-gray-400"
            disabled={isLoading}
          >
            {isLoading ? "در حال چاپ..." : "چاپ فاکتور"}
          </button>
          {errorMessage && (
            <div className="mt-4 text-red-600">{errorMessage}</div>
          )}
          {isNotDelivered && (
            <>
              <button
                onClick={handleDeliverPurchase}
                disabled={deliverLoading}
                className="px-6 py-3 mx-1 text-white bg-green-600 rounded-lg shadow hover:bg-green-700 disabled:bg-gray-400"
              >
                {deliverLoading ? "در حال تحویل..." : "تحویل خرید"}
              </button>

              {deliverError && (
                <div className="mt-3 text-red-600">{deliverError}</div>
              )}

              {deliverSuccess && (
                <div className="mt-3 text-green-600">
                  خرید با موفقیت تحویل شد
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Withdraws;
